import type { IdentifierModel, IdentifierValueModel, UUID } from "@overmap-ai/common"
import { type Field, FieldSchema, type ValueOfField } from "@overmap-ai/forms"

import { getFieldCsvColumnHeader, getIdentifierCsvColumnHeader } from "../utils"
import type { CSVImporterDataColumnDef, SupportedCSVImporterFields } from "./typings"

export const getCSVImportColumnDefOfField = (
	field: SupportedCSVImporterFields,
): CSVImporterDataColumnDef<ValueOfField<typeof field>> => {
	switch (field.type) {
		case "boolean":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					const booleanStringMap: Record<string, boolean> = {
						TRUE: true,
						True: true,
						true: true,
						FALSE: false,
						False: false,
						false: false,
						"": false,
					}

					// TODO: throw here if cant map value

					return booleanStringMap[value]
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		case "checkbox-list":
		case "multi-select":
		case "multi-string":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					return value.split(", ")
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		case "rating":
		case "number":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					return value ? Number(value) : null
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		case "date":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					return value ? new Date(value) : null
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		case "url":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					// TODO: throw here if cant parse to URL
					return value ? new URL(value) : null
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		case "radio":
		case "select":
		case "time":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => {
					return value || null
				},
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
		// string valued fields
		case "qr":
		case "string":
		case "text":
		case "otp":
			return {
				header: getFieldCsvColumnHeader(field),
				required: !!field.required,
				transformValue: (value) => value,
				validateValue: (value) => {
					return field.isValue(value)
				},
			}
	}
}

export const getCSVImportColumnDefsOfFields = (fields: SupportedCSVImporterFields[]) => {
	const ret: Record<Field["id"], CSVImporterDataColumnDef<ValueOfField<(typeof fields)[number]>>> = {}

	for (const field of fields) {
		ret[field.id] = getCSVImportColumnDefOfField(field)
	}

	return ret
}

export const getCSVImportColumnDefsOfFieldSchema = (fieldSchema: FieldSchema) => {
	return getCSVImportColumnDefsOfFields(getSupportedCSVImporterFields(fieldSchema.getFields()))
}

export const getSupportedCSVImporterFields = (fields: Field[]): SupportedCSVImporterFields[] => {
	return fields.filter((field) => field.type !== "pass-fail" && field.type !== "upload")
}

export const getCSVImportColumnDefOfIdentifier = <TIdentifier extends UUID<IdentifierModel>>(
	identifier: TIdentifier,
): CSVImporterDataColumnDef<IdentifierValueModel["value"]> => {
	return {
		header: getIdentifierCsvColumnHeader(identifier),
		required: false,
		transformValue: (value) => value,
		validateValue: (_value) => true,
	}
}

export const getCSVImportColumnDefsOfIdentifiers = <TIdentifier extends UUID<IdentifierModel>>(
	identifiers: TIdentifier[],
) => {
	// @ts-expect-error REASON: Generic typing making typescript lie
	const ret: Record<TIdentifier["uuid"], CSVImporterDataColumnDef<IdentifierValueModel["value"]>> = {}

	for (const identifier of identifiers) {
		ret[identifier.uuid] = getCSVImportColumnDefOfIdentifier(identifier)
	}

	return ret
}
